// MACHINERY V2 (Stroje) — THE KATALOG SHEET'S APPLY MODEL (M2.16c).
//
// >>> THIS FILE IS THE SEAM, exactly as fleet/useFleetFilterDraft.ts is the
// fleet's. <<< What is STAGED and what is LIVE is decided here and nowhere else;
// CatalogFilterSheet.tsx and catalogSheetSections.tsx call named setters and
// render what this hook hands them.
//
// ============================================================
//  EVERYTHING IN THIS SHEET IS STAGED — THERE IS NO LIVE HALF
// ============================================================
// The fleet's sheet splits six sections into staged FACETS and live MODE
// controls (group-by / view / scope). The katalog sheet has no mode controls at
// all: every one of its eight sections is a FILTER FACET, so all eight stage
// into `draft` and are applied together by the footer's primary. Zavriet, Esc
// and the backdrop discard, and the next open re-seeds from applied state.
//
// This also buys something the fleet did not need: every katalog filter is a
// SERVER param, so a live-applying sheet would fire a `browseCatalogModels`
// request per tap, behind a sheet that covers the results those requests are
// changing. Staging turns eight taps into one request.
//
// ============================================================
//  `search` IS DELIBERATELY NOT IN THE DRAFT — AND THIS IS A REAL BUG, NOT
//  TIDINESS
// ============================================================
// The search box lives in pattern B's row 1, OUTSIDE the sheet, and commits on a
// 300 ms debounce (useCatalogBrowse.ts `SEARCH_DEBOUNCE_MS`). Type "mazak" and
// open the sheet inside that window and the debounce lands AFTER the draft was
// seeded: a commit that wrote the draft's whole filter half would then write
// `search: undefined` back over the search the user had just entered and watched
// take effect. So the sheet commits `CATALOG_FILTER_KEYS` MINUS `search`, and
// the search box keeps sole ownership of its own value. Same defect family as
// the stale merge base fleetActiveFilters.ts:21-35 records.
//
// ============================================================
//  SEEDING — ON THE OPEN TRANSITION, AND ONLY THERE
// ============================================================
// Taken in the RENDER BODY keyed on `open !== prevOpen` (React's documented
// "adjusting state when a prop changes" pattern), not in an effect — the same
// choice, for the same two reasons, as useFleetFilterDraft.ts: it is exact about
// the TRIGGER (an open transition, not a dependency change), and it is
// synchronous, so the first frame of the open sheet already shows the seeded
// draft instead of one frame of the previous one.
//
// RULE (d) — A SHEET HOLDING USER WORK NEVER DISCARDS IT ON FAILURE. Pending
// selections ARE user work. Nothing below writes `draft` except a user gesture
// and the open transition, so a re-render, a failed browse request and a parent
// state change all leave the draft exactly as the user left it.
//
// >>> WHICH IS ALSO WHY THIS HOOK IS CALLED ON THE PAGE, NOT IN THE SHEET
// (review finding F4). <<< It used to be called from CatalogFilterSheet, which
// CatalogMobileChrome mounts and CatalogShell UNMOUNTS at 1280 — so rotating a
// tablet with the sheet open destroyed the staged draft, the one thing the
// paragraph above says must survive everything. It is now called by
// CatalogBrowseV2 alongside `legendOpen`, above every breakpoint branch, exactly
// as CatalogAddDialogV2 calls `useCatalogAddForm` above ITS shell branch and for
// the identical reason. The open flag was hoisted with it: an open sheet whose
// draft outlives the rotation but whose `open` does not is the same loss.
//
// ============================================================
//  CLEAR-ALL OWNS MORE THAN THE DRAFT (review item 3)
// ============================================================
// The header action reads "Zrusit vsetko", and a user who taps it expects the
// section to look untouched. Two pieces of that section are NOT in the draft:
//   · `brandSearch` — live state on `useCatalogBrowse`, filtering the visible
//     OPTION list (catalogSheetBrands.tsx's header explains why it is not
//     staged). Clearing only the draft left the box full of text and the brand
//     list still narrowed by it;
//   · the brand list's "show all" expander.
// The chip row's clear-all already did the first correctly
// (catalogActiveFilters.ts:172-176), so the two surfaces had two behaviours for
// one verb. Both are reset now, through the caller's `onClearBrandList` — this
// hook owns neither and must not pretend to.
//
// >>> AND THE EXPANDER IS THE PAGE'S ONE, NOT A SECOND ONE. <<< The rail and
// the sheet render the same brand list through the same `visibleFacetRows`, so
// "show all N brands" is ONE piece of state; it was local `useState` inside
// catalogSheetBrands.tsx, which is both a duplicate and unreachable from any
// clear-all. It rides this api purely as a pass-through, because the api is
// already threaded to the section that needs it and the alternative is the
// same two props drilled through five components.
//
// >>> THE FOOTER PRIMARY CARRIES NO COUNT, AND THAT IS DATA HONESTY. <<< The
// fleet's "Zobrazit n strojov" previews the pending selection because its fleet
// is an in-memory array it can re-filter for free. The katalog's result count
// comes from the SERVER (`browseCatalogModels` -> `result.totalCount`,
// useCatalogBrowse.ts), so there is no honest n to print without firing the very
// request the staging exists to avoid — and counting the loaded PAGE would print
// a page size under a catalog label. The verb is therefore "apply", not "show
// n". Do not wire the current total in: it describes the pre-apply state.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import { CatalogBrowseQuery } from "../../models/models";
import {
  CATALOG_FILTER_KEYS,
  clearCatalogFilters,
  toggleCatalogFlag,
} from "./catalogQuery";

/** What the sheet owns: every catalog filter except the one the search box owns
 *  — see the header. Derived FROM the canonical list so a filter added there is
 *  automatically the sheet's too. */
const SHEET_KEYS: string[] = CATALOG_FILTER_KEYS.filter((k) => k !== "search");

/** Copies only SHEET_KEYS across, deleting rather than assigning `undefined` so
 *  the committed object has the same SHAPE `clearCatalogFilters` produces. */
const applySheetKeys = (target: CatalogBrowseQuery, source: CatalogBrowseQuery): void => {
  const t = target as unknown as Record<string, unknown>;
  const s = source as unknown as Record<string, unknown>;
  SHEET_KEYS.forEach((key) => {
    if (s[key] === undefined) delete t[key];
    else t[key] = s[key];
  });
};

export interface CatalogFilterDraftApi {
  draft: CatalogBrowseQuery;
  /** Merge a partial into the staged draft. */
  patch: (patch: Partial<CatalogBrowseQuery>) => void;
  /** DR-1 / DR-2 booleans — flips to `undefined`, never `false`. */
  toggleFlag: (key: "oemReady" | "machineIdIntegrated") => void;
  /** Header action. The model SEARCH box is untouched — it is live and visible
   *  in row 1 and this sheet does not own it — but the brand option list IS
   *  reset, because it is part of the section the verb names (see the header). */
  clearAll: () => void;
  commit: () => void;
  /** The VYROBCA list's "show all brands" expander — the PAGE's state, relayed
   *  so the rail and the sheet cannot disagree about it. */
  brandsExpanded: boolean;
  setBrandsExpanded: (expanded: boolean) => void;
}

export interface UseCatalogFilterDraftArgs {
  open: boolean;
  query: CatalogBrowseQuery;
  setQuery: React.Dispatch<React.SetStateAction<CatalogBrowseQuery>>;
  /** Run by `clearAll` for the brand OPTION-LIST state this hook does not own —
   *  the live `brandSearch` box and the expanders. Required, not optional: a
   *  caller that forgets it re-opens review item 3. */
  onClearBrandList: () => void;
  /** The page's "show all brands" expander, passed straight through — see the
   *  header. `onClearBrandList` is what resets it. */
  brandsExpanded: boolean;
  setBrandsExpanded: (expanded: boolean) => void;
}

export const useCatalogFilterDraft = ({
  open,
  query,
  setQuery,
  onClearBrandList,
  brandsExpanded,
  setBrandsExpanded,
}: UseCatalogFilterDraftArgs): CatalogFilterDraftApi => {
  const [draft, setDraft] = useState<CatalogBrowseQuery>(query);
  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    // Seed on the way IN only. Discard is the ABSENCE of a write: closing leaves
    // the stale draft in state and the next open overwrites it, which is why
    // there is no `discard()` to forget to call.
    if (open) setDraft(query);
  }

  const patch = (next: Partial<CatalogBrowseQuery>) =>
    setDraft((prev) => ({ ...prev, ...next }));

  const toggleFlag = (key: "oemReady" | "machineIdIntegrated") =>
    // Reuses the canonical helper so the omit-when-unchecked rule is not
    // restated here. Its `page: 1` reset is harmless on a draft — the commit
    // sets page 1 anyway.
    setDraft((prev) => toggleCatalogFlag(prev, key));

  const clearAll = () => {
    setDraft((prev) => clearCatalogFilters(prev));
    // The non-draft half of the same verb — see the header block.
    onClearBrandList();
  };

  const commit = () =>
    setQuery((prev) => {
      // MERGED ONTO `prev`, NOT ONTO THE SEED. `prev` is the freshest query at
      // commit time, so a category tab change or a search debounce that landed
      // while the sheet was open survives; only the keys this sheet owns are
      // overwritten.
      const next: CatalogBrowseQuery = { ...prev, page: 1 };
      applySheetKeys(next, draft);
      return next;
    });

  return {
    draft,
    patch,
    toggleFlag,
    clearAll,
    commit,
    brandsExpanded,
    setBrandsExpanded,
  };
};

export default useCatalogFilterDraft;
